import { useCallback, useEffect, useState } from "react";
import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "../_app/lib/query";
import { useFeedbackAlerts, useQueryParam } from "../_app/hooks";
import { checkMFAStatus, disableMfa, getUserpoolPasswordPolicy } from "./api";
import { PasswordPolicy, UserAuthSession } from "./types";
import { extractUserPoolId } from "../_app/lib/amplify";
import { removeContextIdFromStorage, setContextIdToStorage } from "../context/utils";
import { useContextProvider } from "../context/providers/ContextProvider";
import { AuthSession, AuthUser, fetchAuthSession, getCurrentUser, signOut } from "aws-amplify/auth";
import { tryCatch } from "../_app/utils";
import { getSensitiveParam } from "./lib/sensitiveParams";

const transformAuthSession = (session: AuthSession): UserAuthSession | undefined => {
  if (!session.tokens?.accessToken?.toString() || !session.tokens?.idToken?.toString()) {
    return undefined;
  }

  const accessToken = session.tokens.accessToken.toString();
  const idToken = session.tokens.idToken.toString();

  const idTokenPayload = session.tokens.idToken.payload;
  const userSub = idTokenPayload?.sub ?? "";

  const userProfile = {
    firstName: idTokenPayload?.given_name as string,
    lastName: idTokenPayload?.family_name as string,
    username: (idTokenPayload?.["cognito:username"] as string) ?? (idTokenPayload?.username as string),
    email: idTokenPayload?.email as string,
  };

  const expiresAt = session.tokens.idToken.payload?.exp;
  const act = Boolean(session?.tokens?.idToken?.payload?.act);

  return {
    accessToken,
    idToken,
    userSub,
    userProfile,
    expiresAt,
    act,
  };
};

export const useAuthSession = () => {
  return useQuery({
    queryKey: ["authSession"],
    queryFn: () => fetchAuthSession(),

    // Always considered stale - fetch on every use
    staleTime: 0,

    // Amplify caches tokens on its own; keep a minimal gcTime — in v5 `gcTime: 0`
    // drops the cache entry between observer switches and causes refetch loops
    gcTime: 1000,

    select: transformAuthSession,
  });
};

// Resolves the currently signed-in Amplify user once on mount. `isSessionChecked` distinguishes
// "still resolving" from "resolved with no user", so callers can hold rendering until they know.
export const useCurrentAuthUser = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isSessionChecked, setIsSessionChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    tryCatch(getCurrentUser()).then(({ data }) => {
      if (cancelled) return;
      if (data) setUser(data);
      setIsSessionChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { user, isSessionChecked };
};

export const useCognitoUserpoolPolicy = (options?: any) => {
  const userPoolId = extractUserPoolId(window.authConfig.tokenIssuerUrl);

  return useQuery<PasswordPolicy>({
    queryKey: ["cognitoUserpoolPolicy"],
    queryFn: () => getUserpoolPasswordPolicy(userPoolId),
    enabled: Boolean(userPoolId),
    ...options,
  });
};

export function useJWTAuth() {
  const { setFeedbackAlertError } = useFeedbackAlerts();
  const [isLoading, setIsLoading] = useState(true);
  // Prefer the stashed value (stripped from the URL at bootstrap); fall back to the query param.
  const jwtQueryParam = useQueryParam("jwt");
  const jwtParam = getSensitiveParam("jwt") ?? jwtQueryParam;
  const path = useQueryParam("path");
  const contextId = useQueryParam("contextId");

  const handleContextSelection = useCallback(() => {
    setContextIdToStorage(contextId?.toString() || "");
  }, [contextId]);

  const handleSuccess = useCallback(() => {
    setIsLoading(false);
    window.location.replace(`${window.location.origin}${path ? `/${path}` : ""}`);
  }, [path]);

  const setInSession = useCallback(
    async (jwtEncoded: string) => {
      try {
        await impersonateUser(jwtEncoded);
        handleContextSelection();
        handleSuccess();
      } catch (e) {
        setFeedbackAlertError(e);
      }
    },
    [handleSuccess, handleContextSelection, setFeedbackAlertError],
  );

  useEffect(
    function onMount() {
      if (typeof jwtParam === "string") {
        setInSession(jwtParam);
      }
    },
    [jwtParam, setInSession],
  );

  return { isLoading };
}

async function impersonateUser(idJwtToken: string) {
  const keyPrefix = `CognitoIdentityServiceProvider.${window.authConfig?.customerPortalClientId}`;
  localStorage.setItem(`${keyPrefix}.LastAuthUser`, "impersonated_user");
  localStorage.setItem(`${keyPrefix}.impersonated_user.idToken`, idJwtToken);
  localStorage.setItem(`${keyPrefix}.impersonated_user.accessToken`, idJwtToken);
  localStorage.setItem(`${keyPrefix}.impersonated_user.refreshToken`, "");
}

export function useJWTLogout() {
  const keyPrefix = `CognitoIdentityServiceProvider.${window.authConfig?.customerPortalClientId}`;
  localStorage.removeItem(`${keyPrefix}.LastAuthUser`);
  localStorage.removeItem(`${keyPrefix}.manual_user.idToken`);
  removeContextIdFromStorage();
  window.location.replace(window.location.origin);
}

export function useMfaEnabled(options = {}) {
  const { data: session } = useAuthSession();
  const accessToken = session?.accessToken ?? "";
  return useQuery({
    queryKey: ["mfa-enabled", { accessToken }],
    queryFn: () => checkMFAStatus(accessToken),
    ...options,
  });
}

export function useDisableMfa(options?: UseMutationOptions) {
  const { setFeedbackAlertSuccess, setFeedbackAlertError } = useFeedbackAlerts();
  const { data: session } = useAuthSession();
  const accessToken = session?.accessToken ?? "";
  const { refetch: refetchMfaEnabled } = useMfaEnabled(accessToken);
  return useMutation({
    mutationKey: ["disable-mfa", { accessToken }],
    mutationFn: () => disableMfa(accessToken),
    onSuccess: async () => {
      await refetchMfaEnabled();
      setFeedbackAlertSuccess("MFA successfully disabled.");
    },
    onError: () => {
      setFeedbackAlertError("MFA couldn't be disabled.");
    },
    ...options,
  });
}

export const useLogout = () => {
  const { reset: resetContext } = useContextProvider();
  const queryClient = useQueryClient();

  const logout = useCallback(
    async (withRedirect?: boolean) => {
      // Await the real signOut (not the Authenticator dispatch) so the refresh-token revocation
      // request finishes before we clear storage and tear the page down. A failure (e.g. offline)
      // must not block the local logout.
      await tryCatch(signOut());
      resetContext();
      localStorage.clear();
      if (withRedirect) {
        // Full reload tears the app down cleanly and resets all in-memory state. It also avoids
        // the 401 storm that queryClient.clear() triggered by force-refetching app-level queries
        // (context, features, etc.) against the session we just cleared.
        window.location.replace("/login");
        return;
      }
      queryClient.clear();
    },
    [resetContext, queryClient],
  );
  return logout;
};
